const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
  };
  // Cross-domain session fallback (Azure SWA → App Service, Pi Nginx proxy, etc.)
  if (window.__sessionId) {
    headers['X-Session-ID'] = window.__sessionId;
  }
  return headers;
};

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    // Session expired or not authenticated — notify the app to redirect to login
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    const error = await response.json().catch(() => ({ error: 'Network error' }));
    throw new Error(error.error || error.message || 'API request failed');
  }

  const data = await response.json();

  // Handle new API response format
  if (data.success !== undefined) {
    if (!data.success) {
      throw new Error(data.error || 'API request failed');
    }
    return data.data || data;
  }

  // Handle legacy format
  return data;
};

// Helper function for API calls
const apiCall = async (url, options = {}) => {
  const config = {
    credentials: 'include', // Include cookies for session management
    ...options
  };

  // Handle FormData (for file uploads)
  if (config.body instanceof FormData) {
    // Don't set Content-Type for FormData, let browser set it with boundary
    config.headers = {
      ...config.headers,
      // Remove Content-Type to let browser set it
    };
    delete config.headers['Content-Type'];
  } else {
    // Set default headers for JSON requests
    config.headers = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...config.headers
    };

    if (config.body && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }
  }

  const response = await fetch(`${API_BASE_URL}${url}`, config);
  return handleResponse(response);
};

const api = {
  // Vendors
  getVendors: async () => {
    return apiCall('/vendors');
  },

  createVendor: async (data) => {
    return apiCall('/vendors', {
      method: 'POST',
      body: data
    });
  },

  updateVendor: async (id, data) => {
    return apiCall(`/vendors/${id}`, {
      method: 'PUT',
      body: data
    });
  },

  deleteVendor: async (id) => {
    return apiCall(`/vendors/${id}`, {
      method: 'DELETE'
    });
  },

  // Materials
  getMaterials: async () => {
    return apiCall('/materials');
  },

  createMaterial: async (data) => {
    return apiCall('/materials', {
      method: 'POST',
      body: data
    });
  },

  updateMaterial: async (id, data) => {
    return apiCall(`/materials/${id}`, {
      method: 'PUT',
      body: data
    });
  },

  deleteMaterial: async (id) => {
    return apiCall(`/materials/${id}`, {
      method: 'DELETE'
    });
  },

  // Jewelry
  getJewelry: async (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return apiCall(`/jewelry?${searchParams.toString()}`);
  },

  createJewelry: async (data) => {
    return apiCall('/jewelry', {
      method: 'POST',
      body: data
    });
  },

  updateJewelry: async (id, data) => {
    return apiCall(`/jewelry/${id}`, {
      method: 'PUT',
      body: data
    });
  },

  deleteJewelry: async (id) => {
    return apiCall(`/jewelry/${id}`, {
      method: 'DELETE'
    });
  },

  // Categories
  getCategories: async () => {
    return apiCall('/categories');
  },

  createCategory: async (data) => {
    return apiCall('/categories', {
      method: 'POST',
      body: data
    });
  },

  updateCategory: async (id, data) => {
    return apiCall(`/categories/${id}`, {
      method: 'PUT',
      body: data
    });
  },

  deleteCategory: async (id) => {
    return apiCall(`/categories/${id}`, {
      method: 'DELETE'
    });
  },

  // Users
  getUsers: async (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return apiCall(`/users?${searchParams.toString()}`);
  },

  createUser: async (data) => {
    return apiCall('/users', {
      method: 'POST',
      body: data
    });
  },

  updateUser: async (id, data) => {
    return apiCall(`/users/${id}`, {
      method: 'PUT',
      body: data
    });
  },

  deleteUser: async (id) => {
    return apiCall(`/users/${id}`, {
      method: 'DELETE'
    });
  },

  // Authentication
  login: async (username, password) => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include', // Include cookies for session management
      body: JSON.stringify({ username, password })
    });

    return handleResponse(response);
  },

  getCurrentUser: async () => {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include' // Include cookies for session management
    });

    return handleResponse(response);
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include', // Include cookies for session management
      body: JSON.stringify({ currentPassword, newPassword })
    });

    return handleResponse(response);
  },

  logout: async () => {
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Clear any client-side state if needed
  },

  // Estimates
  getEstimates: async (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return apiCall(`/estimates?${searchParams.toString()}`);
  },

  createEstimate: async (data) => {
    return apiCall('/estimates', {
      method: 'POST',
      body: data
    });
  },

  updateEstimate: async (id, data) => {
    return apiCall(`/estimates/${id}`, {
      method: 'PUT',
      body: data
    });
  },

  deleteEstimate: async (id) => {
    return apiCall(`/estimates/${id}`, {
      method: 'DELETE'
    });
  },

  // Statistics and Reports
  getJewelryStatistics: async () => {
    return apiCall('/jewelry/statistics');
  },

  getUserStatistics: async () => {
    return apiCall('/users/statistics');
  },

  getInventoryValuation: async (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return apiCall(`/jewelry/valuation?${searchParams.toString()}`);
  },

  // Search functionality
  searchJewelry: async (searchTerm, params = {}) => {
    const searchParams = new URLSearchParams({ q: searchTerm, ...params });
    return apiCall(`/jewelry/search?${searchParams.toString()}`);
  },

  searchUsers: async (searchTerm, params = {}) => {
    const searchParams = new URLSearchParams({ q: searchTerm, ...params });
    return apiCall(`/users/search?${searchParams.toString()}`);
  },

  // Pricing calculations
  calculateJewelryPricing: async (pricingData) => {
    return apiCall('/jewelry/calculate-pricing', {
      method: 'POST',
      body: pricingData
    });
  },

  // Permissions
  getCustomPermissions: async () => {
    return apiCall('/permissions');
  },

  saveCustomPermissions: async (permissions) => {
    return apiCall('/permissions', {
      method: 'POST',
      body: { permissions }
    });
  },

  resetPermissions: async () => {
    return apiCall('/permissions', {
      method: 'DELETE'
    });
  },

  // Upload Functionality
  uploadFile: async (file, vendorId, profileId, batchName) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('vendor_id', vendorId);
    if (profileId) formData.append('profile_id', profileId);
    if (batchName) formData.append('batch_name', batchName);

    return apiCall('/upload/excel', {
      method: 'POST',
      body: formData,
      headers: {
        // Don't set Content-Type header for FormData, let browser set it
        credentials: 'include'
      }
    });
  },

  getUploadBatches: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/upload/batches${queryString ? '?' + queryString : ''}`);
  },

  getBatchItems: async (batchId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/upload/batches/${batchId}/items${queryString ? '?' + queryString : ''}`);
  },

  getBatchSummary: async (batchId) => {
    return apiCall(`/upload/batches/${batchId}/summary`);
  },

  approveUploadItem: async (itemId) => {
    return apiCall(`/upload/items/${itemId}/approve`, {
      method: 'PUT'
    });
  },

  rejectUploadItem: async (itemId, reason) => {
    return apiCall(`/upload/items/${itemId}/reject`, {
      method: 'PUT',
      body: { reason }
    });
  },

  bulkApproveItems: async (itemIds) => {
    return apiCall('/upload/items/bulk-approve', {
      method: 'POST',
      body: { item_ids: itemIds }
    });
  },

  // Upload Profiles
  getUploadProfiles: async (vendorId = null) => {
    const params = vendorId ? `?vendor_id=${vendorId}` : '';
    return apiCall(`/upload/profiles${params}`);
  },

  getUploadProfile: async (profileId) => {
    return apiCall(`/upload/profiles/${profileId}`);
  },

  createUploadProfile: async (profileData) => {
    return apiCall('/upload/profiles', {
      method: 'POST',
      body: profileData
    });
  },

  updateUploadProfile: async (profileId, profileData) => {
    return apiCall(`/upload/profiles/${profileId}`, {
      method: 'PUT',
      body: profileData
    });
  },

  deleteUploadProfile: async (profileId) => {
    return apiCall(`/upload/profiles/${profileId}`, {
      method: 'DELETE'
    });
  },

  // Stone Management
  getStoneMaterialsNeedingPrices: async () => {
    return apiCall('/stones/pending-prices');
  },

  updateStonePricing: async (materialId, costPrice, salePrice) => {
    return apiCall(`/stones/${materialId}/pricing`, {
      method: 'PUT',
      body: { cost_price: costPrice, sale_price: salePrice }
    });
  },

  bulkUpdateStonePricing: async (updates) => {
    return apiCall('/stones/bulk-pricing', {
      method: 'PUT',
      body: { updates }
    });
  },

  getStoneMaterial: async (code) => {
    return apiCall(`/stones/material/${code}`);
  },

  createStoneMaterial: async (stoneCategory, stoneType, costPrice, salePrice) => {
    return apiCall('/stones/material', {
      method: 'POST',
      body: {
        stone_category: stoneCategory,
        stone_type: stoneType,
        cost_price: costPrice,
        sale_price: salePrice
      }
    });
  },

  getStoneCategories: async () => {
    return apiCall('/stones/categories');
  },

  // Legacy method for backward compatibility
  bulkUploadJewelry: async (items) => {
    // Convert to new format - create a batch and approve items
    const formData = new FormData();
    const file = new Blob([JSON.stringify(items)], { type: 'application/json' });
    formData.append('file', file, 'bulk_items.json');
    formData.append('vendor_id', '1'); // Default vendor
    formData.append('batch_name', `Bulk_Upload_${Date.now()}`);

    const uploadResult = await apiCall('/upload/upload', {
      method: 'POST',
      body: formData,
      headers: {
        credentials: 'include'
      }
    });

    // Auto-approve all items
    if (uploadResult.batch_id) {
      const batchItems = await apiCall(`/upload/batches/${uploadResult.batch_id}/items`);
      if (batchItems && batchItems.length > 0) {
        const itemIds = batchItems.map(item => item.id);
        await apiCall('/upload/items/bulk-approve', {
          method: 'POST',
          body: { item_ids: itemIds }
        });
      }
    }

    return {
      successful: items,
      failed: []
    };
  }
};

// ── Vendor Import (GDS packing list) ─────────────────────────────────────────
export const vendorImportApi = {
  upload: async (file, vendorId) => {
    const formData = new FormData();
    formData.append('file', file);
    if (vendorId) formData.append('vendor_id', vendorId);
    return apiCall('/vendor-import/upload', { method: 'POST', body: formData });
  },
  getBatches: () => apiCall('/vendor-import/batches'),
  getBatchItems: (batchId) => apiCall(`/vendor-import/batches/${batchId}/items`),
  updateItem: (itemId, updates) => apiCall(`/vendor-import/items/${itemId}`, { method: 'PATCH', body: updates }),
  approveItem: (itemId) => apiCall(`/vendor-import/items/${itemId}/approve`, { method: 'POST' }),
  approveSelected: (batchId, itemIds) => apiCall(`/vendor-import/batches/${batchId}/approve-selected`, { method: 'POST', body: { item_ids: itemIds } }),
  deleteItem: (itemId) => apiCall(`/vendor-import/items/${itemId}`, { method: 'DELETE' }),
  deleteBatch: (batchId) => apiCall(`/vendor-import/batches/${batchId}`, { method: 'DELETE' }),
};

export default api;
